import React, { useState } from 'react';
import { baseUrl } from '../../../api/api';

export type CoverSide = 'front' | 'back' | 'fullwrap';

const GENRES = [
  'Fiction', 'Non-Fiction', 'Thriller', 'Romance', 'Fantasy',
  'Sci-Fi', 'Horror', 'Biography', 'Self-Help', "Children's",
  'Poetry', 'Historical', 'Mystery', 'Literary',
];

const STYLES: { id: string; label: string; desc: string; bg: string }[] = [
  { id: 'minimalistic', label: 'Minimalistic', desc: 'Clean, sparse, open space',      bg: 'bg-gray-50 border-gray-200' },
  { id: 'artistic',     label: 'Artistic',     desc: 'Painterly, expressive, vivid',   bg: 'bg-amber-50 border-amber-200' },
  { id: 'typography',   label: 'Typography',   desc: 'Bold lettering texture as art',  bg: 'bg-blue-50 border-blue-200' },
  { id: 'geometric',    label: 'Geometric',    desc: 'Shapes, patterns, structure',    bg: 'bg-purple-50 border-purple-200' },
  { id: 'illusion',     label: 'Illusion',     desc: 'Surreal, optical, mind-bending', bg: 'bg-pink-50 border-pink-200' },
  { id: 'abstract',     label: 'Abstract',     desc: 'Bold color, non-figurative',     bg: 'bg-orange-50 border-orange-200' },
  { id: 'photographic', label: 'Photo-real',   desc: 'Cinematic, realistic scene',     bg: 'bg-cyan-50 border-cyan-200' },
  { id: 'vintage',      label: 'Vintage',      desc: 'Retro textures, aged feel',      bg: 'bg-yellow-50 border-yellow-200' },
  { id: 'cinematic',    label: 'Cinematic',    desc: 'Dark, dramatic, film-like',      bg: 'bg-slate-100 border-slate-300' },
  { id: 'watercolor',   label: 'Watercolor',   desc: 'Soft washes, hand-painted',      bg: 'bg-teal-50 border-teal-200' },
];

const COVER_SIDES: { id: CoverSide; label: string; hint: string }[] = [
  { id: 'front',    label: 'Front',     hint: 'High-impact visual — the hook' },
  { id: 'back',     label: 'Back',      hint: 'Softer top, plain lower area for blurb' },
  { id: 'fullwrap', label: 'Full Wrap', hint: 'Panoramic flow: back → spine → front' },
];

interface GeneratedImage {
  url: string;
  revised_prompt?: string;
}

interface AIGeneratePanelProps {
  bookTitle: string;
  bookAuthor: string;
  bookId: string;
  generationsUsed: number;
  generationsLimit: number;
  onAddImage: (src: string, coverSide: CoverSide) => void;
  onClose: () => void;
  onGenerationsUpdate: (used: number, limit: number) => void;
}

const AIGeneratePanel: React.FC<AIGeneratePanelProps> = ({
  bookTitle, bookAuthor, bookId,
  generationsUsed: initialUsed, generationsLimit: initialLimit,
  onAddImage, onClose, onGenerationsUpdate,
}) => {
  const [coverSide, setCoverSide]       = useState<CoverSide>('front');
  const [description, setDescription]   = useState('');
  const [genre, setGenre]               = useState<string | null>(null);
  const [style, setStyle]               = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults]           = useState<GeneratedImage[]>([]);
  const [error, setError]               = useState<string | null>(null);
  const [generationsUsed,  setGenerationsUsed]  = useState(initialUsed);
  const [generationsLimit, setGenerationsLimit] = useState(initialLimit);

  const remaining   = generationsLimit - generationsUsed;
  const isExhausted = remaining <= 0;
  const canGenerate = !isExhausted && description.trim().length > 10 && genre !== null && style !== null;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setIsGenerating(true);
    setError(null);
    setResults([]);

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${baseUrl}/api/covers/generate-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ bookId, bookTitle, bookAuthor, description: description.trim(), genre, style, coverSide }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data.generationsUsed !== undefined) {
          const used  = data.generationsUsed;
          const limit = data.generationsLimit ?? generationsLimit;
          setGenerationsUsed(used);
          setGenerationsLimit(limit);
          onGenerationsUpdate(used, limit);
        }
        throw new Error((data as any).message || `Server error ${res.status}`);
      }

      setResults(data.images || []);
      if (data.generationsUsed !== undefined) {
        const used  = data.generationsUsed;
        const limit = data.generationsLimit ?? generationsLimit;
        setGenerationsUsed(used);
        setGenerationsLimit(limit);
        onGenerationsUpdate(used, limit);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200" style={{ width: 300 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-gray-900">AI Cover</span>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 uppercase tracking-wide">Beta</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors text-lg leading-none">×</button>
      </div>

      {/* Quota banner */}
      <div className={`flex items-center justify-between px-4 py-2 flex-shrink-0 ${isExhausted ? 'bg-red-50 border-b border-red-100' : 'bg-orange-50 border-b border-orange-100'}`}>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium text-gray-600">
            {isExhausted ? '🚫 Limit reached for this book' : '🎁 Limited-time free offer'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {Array.from({ length: generationsLimit }).map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full border ${i < generationsUsed ? 'bg-orange-400 border-orange-400' : 'bg-white border-gray-300'}`}
            />
          ))}
          <span className={`text-[11px] font-semibold ml-1 ${isExhausted ? 'text-red-600' : 'text-orange-600'}`}>
            {remaining}/{generationsLimit} left
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">

        {/* Cover Side */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Generate For
          </label>
          <div className="flex flex-col gap-1.5">
            {COVER_SIDES.map(s => (
              <button
                key={s.id}
                onClick={() => { setCoverSide(s.id); setResults([]); }}
                className={`flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-all ${
                  coverSide === s.id
                    ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-300'
                    : 'border-gray-200 bg-white hover:border-orange-200'
                }`}
              >
                <span className={`text-sm font-medium ${coverSide === s.id ? 'text-orange-700' : 'text-gray-700'}`}>
                  {s.label}
                </span>
                <span className="text-[10px] text-gray-400 text-right max-w-[140px] leading-tight">{s.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Book Description
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe your book — themes, mood, setting, key imagery. More specific = better cover."
            rows={4}
            className="w-full text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-300 transition-colors placeholder-gray-400"
          />
          <p className="text-[11px] text-gray-400 mt-1">{description.length} chars — aim for 50+</p>
        </div>

        {/* Genre */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Genre</label>
          <div className="flex flex-wrap gap-1.5">
            {GENRES.map(g => (
              <button
                key={g}
                onClick={() => setGenre(genre === g ? null : g)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                  genre === g
                    ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Style */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cover Style</label>
          <div className="grid grid-cols-2 gap-2">
            {STYLES.map(s => (
              <button
                key={s.id}
                onClick={() => setStyle(style === s.id ? null : s.id)}
                className={`text-left p-2.5 rounded-lg border transition-all ${
                  style === s.id
                    ? 'border-orange-400 bg-orange-50 shadow-sm ring-1 ring-orange-300'
                    : `${s.bg} hover:border-orange-300`
                }`}
              >
                <div className="text-xs font-semibold text-gray-800 mb-0.5">{s.label}</div>
                <div className="text-[10px] text-gray-500 leading-tight">{s.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">{error}</div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Result — click to place on canvas
            </label>
            <div className="grid grid-cols-2 gap-2">
              {results.map((img, i) => (
                <button
                  key={i}
                  onClick={() => onAddImage(img.url, coverSide)}
                  className="group relative rounded-lg overflow-hidden border-2 border-transparent hover:border-orange-400 transition-all"
                  title={img.revised_prompt}
                >
                  <img
                    src={img.url}
                    alt={`Generated ${coverSide} cover ${i + 1}`}
                    className={`w-full object-cover ${coverSide === 'fullwrap' ? 'aspect-[16/9]' : 'aspect-[3/4]'}`}
                    onContextMenu={e => e.preventDefault()}
                    onDragStart={e => e.preventDefault()}
                  />
                  {/* Transparent overlay blocks right-click → Save Image on the img element */}
                  <div
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center"
                    onContextMenu={e => e.preventDefault()}
                  >
                    <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/60 px-2 py-1 rounded transition-all">
                      Add to canvas
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2 leading-tight">
              Image will be placed in the {coverSide === 'fullwrap' ? 'full canvas' : `${coverSide} panel`}. Use canvas handles to reframe.
            </p>
          </div>
        )}
      </div>

      {/* Generate button */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-white">
        {isExhausted ? (
          <div className="text-center">
            <p className="text-[12px] font-medium text-red-500 mb-1">
              You've used both free AI generations for this book.
            </p>
            <p className="text-[11px] text-gray-400">
              Upload your own image or use the design tools above.
            </p>
          </div>
        ) : (
          <>
            {!canGenerate && !isGenerating && (
              <p className="text-[11px] text-gray-400 mb-2 text-center">
                Add description, genre, and style to generate
              </p>
            )}
            <button
              onClick={handleGenerate}
              disabled={!canGenerate || isGenerating}
              className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                canGenerate && !isGenerating
                  ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Generating…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  Generate {coverSide === 'front' ? 'Front Cover' : coverSide === 'back' ? 'Back Cover' : 'Full Wrap'}
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AIGeneratePanel;
